import { useSession } from "@blitzjs/auth"
import { useParam } from "@blitzjs/next"
import { june } from "app/misc/june"
import LogRocket from "logrocket"
import { useRouter } from "next/router"
import { useEffect, useState } from "react"
import { IntercomProps, useIntercom } from "react-use-intercom"
import { trpc } from "../trpcClient"

export const useIdentifyUser = () => {
  const router = useRouter()
  const { boot, update, trackEvent } = useIntercom()
  const session = useSession({ suspense: false })
  const applicationId = useParam("applicationId", "number")!
  const { data: user } = trpc.user.legacyGetCurrentUser.useQuery(
    {},
    {
      suspense: false,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    }
  )
  const [isIdentified, setIsIdentified] = useState<boolean>(false)
  const [userData, setUserData] = useState<IntercomProps | null>(null)
  let [referringUrl, setReferringUrl] = useState<string | null>(null)

  const { data: application } = trpc.applications.getApplication.useQuery(
    { applicationId },
    {
      suspense: false,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    }
  )

  const { data: billingData } = trpc.billing.getBillingData.useQuery(
    { applicationId },
    {
      suspense: false,
      enabled: !!applicationId,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    }
  )

  useEffect(() => {
    if (!session.isLoading && user) {
      const params = {
        email: user.email,
        name: user.name || undefined,
        userId: user.id ? `${user.id}` : undefined,
      }
    }
  }, [session, boot, router.pathname, update, user])

  useEffect(() => {
    if (user && !user.isBotUser) {
      // This is an example script - don't forget to change it!
      LogRocket.identify(user.id.toString(), {
        email: user.email,
        ...(user.name ? { name: user.name } : {}),
        createdAt: user.createdAt.toISOString(), // needed for intercom to treat as date
        application_id: applicationId,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  useEffect(() => {
    if (session && user && !user.isBotUser) {

      window.pylon = {
        chat_settings: {
          app_id: "3016d5e1-846d-4920-a1c0-7093acfdef82",
          email: user.email,
          name: user.name,
          account_id: `${process.env.NODE_ENV === "development" ? "dev_" : ""}${
              application?.organizationId || null
            }`,
        },
      };


      // Note
      // ----
      // Intercom rate limits this to 20 times every 30 min. Make sure it doesnt
      // get called too often. Note that it gets sent everytime the user object changes
      // so don't add in properties that change hyper frequently (like 'last page
      // viewed' for example)

      const newUserData: IntercomProps = {
        userId: `${process.env.NODE_ENV === "development" ? "dev_" : ""}${
          user.id || session.userId || -1
        }`,
        email: user.email,
        name: user.name ? user.name : undefined,
        createdAt: user.createdAt.toISOString(), // needed for intercom to treat as date
        customAttributes: {
          web: true,
          web_role: user.role,
          web_nodeEnvironment: process.env.NODE_ENV,
          web_application_id: application ? applicationId : undefined,
          web_application_name: application ? application.name : undefined,
          web_has_payment_method: billingData
            ? billingData.paymentMethods
              ? billingData.paymentMethods.data.length > 0
              : false
            : false,
        },
        ...(application && {
          company: {
            companyId: `${process.env.NODE_ENV === "development" ? "dev_" : ""}${
              application.organizationId
            }`,
            name: application.organization.name,
            customAttributes: {
              web_trial_ends_at: application.organization.trialEndDate,
              web_has_payment_method: billingData
                ? billingData.paymentMethods
                  ? billingData.paymentMethods.data.length > 0
                  : false
                : false,
            },
          },
        }),
      }

      if (JSON.stringify(userData) !== JSON.stringify(newUserData)) {
        setUserData(newUserData)
        if (!isIdentified) {
          boot(newUserData)
          setIsIdentified(true)
        } else {
          update(newUserData)
        }
        const { email, name, createdAt } = newUserData
        june.identify(user.id.toString(), {
          email,
          name,
          createdAt: createdAt?.toString(),
        })
        // @ts-ignore
        if (ko) {
          try {
            // @ts-ignore
            ko.identify({ email, name, createdAt: createdAt?.toString() })
          } catch (e) {}
        }

        if (application) {
          // @ts-ignore
          june.group(application.id.toString(), {
            ...(newUserData.company || {}),
          })
        }
      }

      if (!session.isLoading && router.pathname !== referringUrl) {
        setReferringUrl(router.pathname)
      }
    }
  }, [
    session,
    application,
    user,
    billingData,
    userData,
    router.pathname,
    referringUrl,
    isIdentified,
    boot,
    update,
    applicationId,
    trackEvent,
  ])
}
